// Reactive, localStorage-backed sound preferences (key `dcs.sound`).
//
// The library default is SILENT (enabled:false) — that is the operational-
// surface contract. Surfaces that opt into default-on (the portal, per the
// Q-72=B carve-out) call configureSoundDefaults() once at startup; it seeds
// the defaults ONLY when the user has no stored preference yet, so a user's
// explicit opt-out is never overwritten on the next visit.

import { ref, watch } from 'vue'
import type { SoundCategory, SoundConfig } from './types'

export const STORAGE_KEY = 'dcs.sound'

const LIBRARY_DEFAULT: SoundConfig = {
  enabled: false,
  userSet: false,
  prmOverride: false,
  volume: 0.3,
  categories: {
    interaction: true,
    overlay: true,
    navigation: true,
    notification: true,
    hero: false,
  },
  hintShown: false,
}

function clampVolume(v: unknown): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : LIBRARY_DEFAULT.volume
  return Math.min(0.8, Math.max(0, n))
}

/** Merge a stored/partial object over the library default, defensively. */
function normalize(raw: Partial<SoundConfig> | null | undefined): SoundConfig {
  const base = LIBRARY_DEFAULT
  const cats = (raw?.categories ?? {}) as Partial<Record<SoundCategory, boolean>>
  return {
    enabled: typeof raw?.enabled === 'boolean' ? raw.enabled : base.enabled,
    userSet: typeof raw?.userSet === 'boolean' ? raw.userSet : base.userSet,
    prmOverride: typeof raw?.prmOverride === 'boolean' ? raw.prmOverride : base.prmOverride,
    volume: clampVolume(raw?.volume),
    categories: {
      interaction: cats.interaction ?? base.categories.interaction,
      overlay: cats.overlay ?? base.categories.overlay,
      navigation: cats.navigation ?? base.categories.navigation,
      notification: cats.notification ?? base.categories.notification,
      hero: cats.hero ?? base.categories.hero,
    },
    hintShown: typeof raw?.hintShown === 'boolean' ? raw.hintShown : base.hintShown,
  }
}

function readStored(): SoundConfig | null {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return null
    }
    return normalize(JSON.parse(raw) as Partial<SoundConfig>)
  } catch {
    return null
  }
}

const stored = readStored()

/** The single reactive config instance shared across every useUiSound() call. */
export const config = ref<SoundConfig>(stored ?? normalize(null))

/** True when the user already has a persisted preference (do not re-seed). */
let hasPersisted = stored !== null

function persist(next: SoundConfig): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    hasPersisted = true
  } catch {
    /* storage full / disabled — keep running in-memory */
  }
}

// Persist every change (deep — categories is nested).
watch(config, (next) => persist(next), { deep: true })

/**
 * Seed surface-level defaults (e.g. portal default-ON at subtle gain) WITHOUT
 * clobbering an existing user preference. Idempotent and safe to call once at
 * app startup before the first useUiSound().
 */
export function configureSoundDefaults(defaults: Partial<SoundConfig>): void {
  if (hasPersisted) {
    return
  }
  config.value = normalize({ ...config.value, ...defaults })
  // First seed is written eagerly so the mute UI reflects a real stored value.
  persist(config.value)
}

/** Cross-tab sync: mirror external `dcs.sound` writes into the reactive config. */
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      try {
        config.value = normalize(JSON.parse(e.newValue) as Partial<SoundConfig>)
        hasPersisted = true
      } catch {
        /* ignore malformed external write */
      }
    }
  })
}
